import type { DrawContext } from './draw';
import { isShared, namedBy, ringGeometry } from './nodes';
import type { ShownNode } from './nodes';
import type { OffSwitches } from './web';

/*
 * THE NAMES, PAINTED — the other half of the node layer.
 *
 * Ported from `reference/universe-v8/engine.js` — `drawStatic` lines 1588–1638. `nodes.ts` decided
 * WHAT is on screen and how bright; this decides what each one looks like.
 *
 * A NAME SAYS THREE THINGS ABOUT ITSELF AT A GLANCE, and they are independent:
 *
 *   its SHAPE    what kind of thing it is — a person, a place, a text, a thing not agreed to exist
 *   its COLOUR   which scripture names it most, or dark with a pale rim when several share it
 *   its RING     one arc per scripture that names it, in the wheel's own order
 *
 * The ring is the one that matters most and it is drawn last, over everything. A figure three
 * traditions name carries three arcs, and the reader can count them without opening anything —
 * which is the whole argument of the map made visible on a single disc.
 *
 * SHARED NAMES GO DARK, NOT BRIGHT. A figure several texts hold is drawn as a hole with a pale rim
 * rather than in any one text's colour, because colouring it would be picking one — and the arcs
 * around it already say who holds it. It reads as an opening rather than as an object.
 *
 * THEIR KINDS ARE SINGLE LETTERS AND OURS ARE WORDS, so the mapping is an argument. A switch on
 * `'x' | 's' | 'd' | 'l' | 'e'` would be their roster's own vocabulary compiled into our renderer —
 * exactly the tunnel vision rule 2 exists to stop.
 */

/** Their turn, in radians, and their quarter turn — written out as they write them. */
const TAU = 6.2832;
const QUARTER = 1.5708;

/**
 * The shapes a name can take.
 *
 * Named for what they MEAN rather than for how they look, because the meaning is what a caller maps
 * its own kinds onto: `absent` is a thing not everybody agrees exists, `eyed` is a thing with an
 * inside, and so on.
 */
export type NodeShape = 'absent' | 'hollow' | 'eyed' | 'square' | 'diamond' | 'disc';

/** What to paint with. Palette, thresholds and the kind mapping are all the caller's. */
export interface NodePaint {
  palette: Readonly<Record<string, string>>;
  /** How many scriptures make a name shared. Theirs is 3 of four; ours would be 3 of ten. */
  sharedAtLeast: number;
  traditionsOfKey: Readonly<Record<string, readonly string[]>>;
  /** Which shape a kind is drawn as. Ours are words, theirs are letters. */
  shapeOf: (kind: string) => NodeShape;
  /**
   * The weight at which a name earns a halo with nobody standing on it.
   *
   * Theirs is 7 of a scale topping out at 10. A weight is data twice over — the scale is ours, and
   * so is what counts as heavy on it.
   */
  haloWeight: number;
  /**
   * The order the ring's arcs are laid out in, which is the wheel's order.
   *
   * Theirs is their four corpus keys; ours is ten in `petal` order. Passed in so the arcs around a
   * disc run the same way round as the wedges behind it — a reader who learns one has learned both.
   */
  corpusOrder: readonly string[];
  /** What the reader switched off, so a ring arc for a hidden scripture is dimmed rather than cut. */
  off: OffSwitches;
}

/** The colours a disc takes. Look, not data. */
const SHARED_CORE = '#06070f';
const SHARED_RIM = 'rgba(222,234,255,.62)';
const UNKNOWN_CORE = '#c3ccdf';
const SHARED_HALO = '#93a8cf';
const ABSENT_STROKE = 'rgba(190,212,255,.5)';
const PUPIL_SHARED = 'rgba(230,240,255,.85)';
const PUPIL_PLAIN = '#06070f';

/** Below this a name is too small to carry a halo at all. */
const HALO_MIN_R = 4.5;
/** How far the halo reaches, and how faint it is. */
const HALO_SPAN = 2;
const HALO_ALPHA_SHARED = 0.13;
const HALO_ALPHA_PLAIN = 0.1;

/** How much of the disc each shape fills. */
const R_DISC = 0.92;
const R_HOLLOW = 0.78;
const R_SQUARE = 0.8;
const R_DIAMOND = 0.98;
const R_ABSENT = 0.92;

/** The stroke widths, each a share of the radius with a floor. */
const W_ABSENT_MIN = 0.6;
const W_ABSENT_SHARE = 0.028;
const W_HOLLOW_MIN = 1;
const W_HOLLOW_SHARE = 0.2;
const W_RIM_MIN = 1;
const W_RIM_SHARE = 0.1;

/** The rim is only drawn once a name is big enough to show one. */
const RIM_MIN_R = 2.6;

/** How much fainter the outline of an unagreed name is. */
const ABSENT_ALPHA = 0.5;

/** The square's corner rounding. */
const SQUARE_CORNER_SHARE = 0.28;

/** The pupil: how big, how bright, and how small a name may be and still have one. */
const PUPIL_MIN_R = 5;
const PUPIL_MIN = 1.4;
const PUPIL_SHARE_SHARED = 0.17;
const PUPIL_SHARE_PLAIN = 0.26;
const PUPIL_ALPHA_SHARED = 0.9;
const PUPIL_ALPHA_PLAIN = 0.5;

/** The gap between the ring's arcs, so they read as several and not as one circle. */
const RING_GAP_MAX = 0.3;
const RING_GAP_SPAN = 1.05;

/** How much fainter a ring arc is when the roster is unsure, or the reader has switched it off. */
const RING_UNVERIFIED = 0.7;
const RING_FILTERED = 0.18;

/**
 * Paint everything `shownNodes` listed, in the order it listed them.
 *
 * One pass, no batching — unlike the stars and the strands, because every disc here differs in
 * colour, alpha and shape, so there is no state to share between them. The saving in this layer is
 * that there are only ever a few hundred of these; the ten thousand are in `sky-draw.ts`, which
 * does batch.
 */
export function drawNodes(ctx: DrawContext, shown: readonly ShownNode[], paint: NodePaint): void {
  for (const s of shown) {
    const { node, sx, sy, r, a } = s;
    const corpora = namedBy(node, paint.palette);
    /* TRADITIONS, not texts — `sharedAtLeast` counts the first and this counted the second. The rule
       itself lives in `nodes.ts`; this file used to restate it, which is how the tested copy came to
       be the one nothing drew. */
    const shared = isShared(node, paint);
    const shape = paint.shapeOf(node.kind);
    const core = shared ? SHARED_CORE : paint.palette[node.dom] ?? UNKNOWN_CORE;
    const rim = shared ? SHARED_RIM : null;
    ctx.globalAlpha = a;

    /* THE HALO: somebody is standing here, or the name is heavy enough to be worth finding. Never
       on an unagreed name — a halo would assert a presence the shape is busy denying. */
    if (r > HALO_MIN_R && shape !== 'absent' && (node.pop > 0 || node.w >= paint.haloWeight)) {
      ctx.globalAlpha = a * (shared ? HALO_ALPHA_SHARED : HALO_ALPHA_PLAIN);
      ctx.fillStyle = shared ? SHARED_HALO : core;
      ctx.beginPath();
      ctx.arc(sx, sy, r * HALO_SPAN, 0, TAU);
      ctx.fill();
      ctx.globalAlpha = a;
    }

    ctx.fillStyle = core;
    ctx.strokeStyle = core;
    ctx.beginPath();
    drawShape(ctx, s, shape, shared, rim, core);
    drawRing(ctx, s, corpora, paint);
  }
  /* Handed back opaque, so the next layer does not inherit the last name's alpha. */
  ctx.globalAlpha = 1;
}

/** The pale ring a shared name carries, when it is big enough to show one. */
function sharedRim(ctx: DrawContext, r: number, shared: boolean, rim: string | null): void {
  if (!shared || r <= RIM_MIN_R || rim === null) return;
  ctx.lineWidth = Math.max(W_RIM_MIN, r * W_RIM_SHARE);
  ctx.strokeStyle = rim;
  ctx.stroke();
}

/** One name's own shape. The path is already open when this is called, exactly as theirs is. */
function drawShape(ctx: DrawContext, s: ShownNode, shape: NodeShape, shared: boolean, rim: string | null, core: string): void {
  const { sx, sy, r, a } = s;
  if (shape === 'absent') {
    /* An outline and nothing inside it — a thing named but not agreed to be a thing. */
    ctx.globalAlpha = a * ABSENT_ALPHA;
    ctx.strokeStyle = ABSENT_STROKE;
    ctx.lineWidth = Math.max(W_ABSENT_MIN, r * W_ABSENT_SHARE);
    ctx.arc(sx, sy, r * R_ABSENT, 0, TAU);
    ctx.stroke();
    ctx.globalAlpha = a;
    return;
  }
  if (shape === 'hollow') {
    /* A thick ring with nothing in it: a container rather than a figure. */
    ctx.arc(sx, sy, r * R_HOLLOW, 0, TAU);
    ctx.lineWidth = Math.max(W_HOLLOW_MIN, r * W_HOLLOW_SHARE);
    ctx.strokeStyle = shared && rim !== null ? rim : core;
    ctx.stroke();
    return;
  }
  if (shape === 'eyed') {
    ctx.arc(sx, sy, r * R_DISC, 0, TAU);
    ctx.fill();
    sharedRim(ctx, r, shared, rim);
    /* A pupil, once there is room for one — light in a shared name, dark in a claimed one. */
    if (r > PUPIL_MIN_R) {
      ctx.globalAlpha = shared ? a * PUPIL_ALPHA_SHARED : a * PUPIL_ALPHA_PLAIN;
      ctx.fillStyle = shared ? PUPIL_SHARED : PUPIL_PLAIN;
      ctx.beginPath();
      ctx.arc(sx, sy, Math.max(PUPIL_MIN, r * (shared ? PUPIL_SHARE_SHARED : PUPIL_SHARE_PLAIN)), 0, TAU);
      ctx.fill();
      ctx.globalAlpha = a;
      ctx.fillStyle = core;
    }
    return;
  }
  if (shape === 'square') {
    const q = r * R_SQUARE;
    const corner = Math.min(q * 0.5, r * SQUARE_CORNER_SHARE);
    /* Their own feature test. A square corner is a worse shape, not a broken one. */
    if (ctx.roundRect) ctx.roundRect(sx - q, sy - q, q * 2, q * 2, corner);
    else ctx.rect(sx - q, sy - q, q * 2, q * 2);
    ctx.fill();
    sharedRim(ctx, r, shared, rim);
    return;
  }
  if (shape === 'diamond') {
    const q = r * R_DIAMOND;
    ctx.moveTo(sx, sy - q);
    ctx.lineTo(sx + q, sy);
    ctx.lineTo(sx, sy + q);
    ctx.lineTo(sx - q, sy);
    ctx.closePath();
    ctx.fill();
    sharedRim(ctx, r, shared, rim);
    return;
  }
  ctx.arc(sx, sy, r * R_DISC, 0, TAU);
  ctx.fill();
  sharedRim(ctx, r, shared, rim);
}

/**
 * ONE ARC PER SCRIPTURE THAT NAMES IT, starting due up and running round in the wheel's order.
 *
 * Due up rather than from the right, so the first arc is where the first wedge is — the disc is a
 * small copy of the whole map's compass, and a reader who has learned one has learned the other.
 *
 * A scripture the reader has switched off is DIMMED to a fifth rather than dropped: the arcs are a
 * count, and a count that changes with the filter would be answering a different question.
 */
function drawRing(ctx: DrawContext, s: ShownNode, corpora: readonly string[], paint: NodePaint): void {
  const { node, sx, sy, r, a } = s;
  const ring = ringGeometry(r, corpora.length);
  if (ring === null) return;
  const step = TAU / corpora.length;
  const gap = Math.min(RING_GAP_MAX, RING_GAP_SPAN / corpora.length);
  /* Laid out in the WHEEL's order, not the order the roster happens to list them in. */
  const arcs = paint.corpusOrder.filter(k => corpora.includes(k));
  ctx.lineWidth = ring.lw;
  ctx.lineCap = 'butt';
  arcs.forEach((k, i) => {
    ctx.globalAlpha = a * (node.verified ? 1 : RING_UNVERIFIED) * (paint.off[`c_${k}`] === true ? RING_FILTERED : 1);
    ctx.strokeStyle = paint.palette[k] ?? UNKNOWN_CORE;
    ctx.beginPath();
    ctx.arc(sx, sy, ring.rr, i * step - QUARTER + gap * 0.5, (i + 1) * step - QUARTER - gap * 0.5);
    ctx.stroke();
  });
  ctx.globalAlpha = a;
}
